import {Component, Input, inject} from '@angular/core';
import {FeatureManipulationService, ShopDataService} from '@webu/data';
import {buildTrackingString} from '@ox/utils';
import {BML} from '@ox/utils';
import {MyPlan, CtaType, pageTypes, FeatureCopy} from '@ox/typings';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {AboutPlanModalComponent} from '../../../components/about-plan-modal/about-plan-modal.component';
import {OX_KEYS} from '@ox/config';

@Component({
  selector: 'webu-plan-feature-list',
  templateUrl: './plan-feature-list.component.html'
})
export class PlanFeatureListComponent {
  bsModalRef?: BsModalRef;
  featuresHidden = false;
  private bml = inject(BML);
  brand = this.bml.brand;
  isBundleProduct = false;
  private _plan: MyPlan;
  
  @Input() 
  set plan(value: MyPlan) {
    this._plan = value;
    this.isBundleProduct = this.shopDataService.getProductCopyByCode(this._plan.planCode)?.isBundledProduct ?? false;
    this.featuresHidden = Boolean(this._plan.transitionTo);
  }

  get plan(): MyPlan {
    return this._plan;
  }

  trackingString = (cta: CtaType, args: string[]) => buildTrackingString(pageTypes.myPlans, cta, args);

  constructor(
    private featureManipulationService: FeatureManipulationService,
    private shopDataService: ShopDataService,
    private modalService: BsModalService
    ) {}

  getHRClass(): string {
    return this.plan.templateType === 'TRANSITION' || this.plan.transitionTo ? `${this.brand}-hr my-3` : `${this.brand}-hr my-3`;
  }

  openModal(category: string) {
    const features = this.getFeatures(category);
    if (!features) throw new Error('No features found');
    this.bsModalRef = this.modalService.show(AboutPlanModalComponent, {
      initialState: {
        features: this.getFeatures(category) ?? [],
        billingCadence: this.plan.billingCadence,
        planName: this.getFeatureCategory(category)
      },
      class: 'modal-lg modal-dialog-centered align-items-end align-items-md-center about-plan-modal mb-0 p-0'
    });
  }

  getPlanFeatureCategories() {
    return this.isBundleProduct ? this.plan.features?.map(feat => feat.category) ?? [] : [this.plan.planName];
  }

  getFeatures(category: string): FeatureCopy[] {
    // Get all features for standalone plans
    if (!this.isBundleProduct) {
      return this.plan.features?.reduce((featureList: FeatureCopy[], feat) => featureList.concat(this.getFeaturesForCategory(feat.category) ?? []), []) ?? [];

    // Return only the features for the current category for bundle plans
    } else {
      return  this.getFeaturesForCategory(category);
    }
  }

  getDescription(category: string) {
    // For bundled plans In-Vehicle Data is not a data plan, have to use copy description from product code
    // of a data plan that US and CA both have
    if (this.plan.features?.find((feature) => feature.category === category
    )?.isData) {
      return this.shopDataService.getProductCopyByCode('GM 30-Day Unlimited Data Plan')?.description;
    }

    let productKey = this.isBundleProduct ? category : this.plan.planName;

    const mismatchingCategoryAndProductNames = new Map<string, string>([
      [OX_KEYS.MY_PLANS.CONNECT_PLUS_FEATURE_KEY, OX_KEYS.MY_PLANS.CONNECT_PLUS_PRODUCT_KEY]
    ]); //Feature category name does not match with product name

    if (mismatchingCategoryAndProductNames.has(productKey)) {
      productKey = this.shopDataService.getProductCopyByCode(mismatchingCategoryAndProductNames.get(productKey) ?? '')?.name ?? productKey; //Get product name based on market/lang
    }

    return this.shopDataService.getProductCopyByName(productKey)?.description ?? '';
  }

  getFeaturesForCategory(category: string): FeatureCopy[] {
    const planFeatureCopy = this.shopDataService.getProductCopyByCode(this.plan.planCode)?.features ?? [];
    const qualifiedFeatures = this.plan.features?.find((feature) => feature.category === category);
    if (!qualifiedFeatures) return [];
    return this.featureManipulationService.getQualifiedFeatureCopy(planFeatureCopy, qualifiedFeatures);
  }

  getFeatureCategory(category: string) {
    if (this.isBundleProduct)
      return category;
    return this.plan.planName;
  }

  toggleFeatures() {
    this.featuresHidden = !this.featuresHidden;
  }

  getGenericCopy(id: string) {
    const variableMap = {plan: this.plan.planName};
    return this.shopDataService.getGenericCopy(id, variableMap);
  }

  featureHasData(category: string): boolean {
    const features = this.plan.features?.find((feature) => feature.category === category);
    return (features?.isData || features?.codes.some((feat: any) => feat?.isData === true)) ?? false;
  }

}
